import { buildAnalyticsUrl } from "../config/api";

function downloadBlob(filename, blob) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function escapeCsv(value) {
    const text = String(value ?? "");
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
}

export function downloadAnalyticsCsv(dashboard, report = "summary") {
    if (report === "atrisk") {
        const headers = ["Student", "Class", "Roll", "Risk score", "Risk level", "Attendance %", "Average mark", "Fee balance", "Dropout risk", "Signals"];
        const rows = (dashboard?.atRiskStudents || []).map((row) =>
            [
                row.name,
                row.className,
                row.rollNum,
                row.riskScore,
                row.riskLevel,
                row.attendancePct,
                row.averageMark,
                row.feeBalance,
                row.dropoutRisk ? "Yes" : "No",
                (row.dropoutSignals || []).join("; "),
            ].map(escapeCsv).join(",")
        );
        const csv = `\uFEFF${[headers.map(escapeCsv).join(","), ...rows].join("\n")}`;
        downloadBlob("analytics-at-risk.csv", new Blob([csv], { type: "text/csv;charset=utf-8" }));
        return;
    }

    const summary = dashboard?.summary || {};
    const headers = ["Metric", "Value"];
    const rows = [
        ["Students", summary.studentCount],
        ["Overall attendance %", summary.overallAttendance],
        ["Overall average mark", summary.overallAverageMark],
        ["Fee collection rate %", summary.feeCollectionRate],
        ["At-risk students", summary.atRiskCount],
        ["Dropout risk flagged", summary.dropoutRiskCount],
        ["Special needs on file", summary.specialNeedsCount],
    ].map((row) => row.map(escapeCsv).join(","));
    const csv = `\uFEFF${[headers.map(escapeCsv).join(","), ...rows].join("\n")}`;
    downloadBlob("analytics-summary.csv", new Blob([csv], { type: "text/csv;charset=utf-8" }));
}

export function downloadAnalyticsPdf(dashboard, title = "Analytics report") {
    const lines = [
        title,
        `Generated: ${dashboard?.generatedAt || new Date().toISOString()}`,
        "",
        `Students: ${dashboard?.summary?.studentCount ?? "—"}`,
        `Overall attendance: ${dashboard?.summary?.overallAttendance ?? "—"}%`,
        `Overall average mark: ${dashboard?.summary?.overallAverageMark ?? "—"}`,
        `Fee collection rate: ${dashboard?.summary?.feeCollectionRate ?? "—"}%`,
        `At-risk students: ${dashboard?.summary?.atRiskCount ?? "—"}`,
        "",
        "At-risk students",
    ];
    for (const row of dashboard?.atRiskStudents || []) {
        lines.push(
            `${row.name} (${row.className}) — risk ${row.riskScore}, attendance ${row.attendancePct}%, avg ${row.averageMark}${row.dropoutRisk ? " [dropout risk]" : ""}`
        );
    }

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
<style>body{font-family:Arial,sans-serif;padding:24px;color:#111}h1{font-size:20px}pre{white-space:pre-wrap;font-size:12px;line-height:1.5}</style></head>
<body><h1>${title}</h1><pre>${lines.join("\n").replace(/</g, "&lt;")}</pre></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (win) {
        win.onload = () => {
            win.focus();
            win.print();
        };
    } else {
        downloadBlob("analytics-report.html", blob);
    }
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download analytics export via API (works with Supabase Edge Functions). */
export async function downloadAnalyticsFromApi({ schoolId, academicYear, term, classId, format = "csv", report = "summary" }) {
    const params = new URLSearchParams({ format, report });
    if (academicYear) params.set("academicYear", academicYear);
    if (term) params.set("term", term);
    if (classId) params.set("classId", classId);
    const response = await fetch(buildAnalyticsUrl(`/Analytics/${schoolId}/export?${params.toString()}`));
    if (!response.ok) throw new Error("Export failed");
    const blob = await response.blob();
    const ext = format === "pdf" ? "pdf" : "csv";
    downloadBlob(`analytics-${report}.${ext}`, blob);
}
